import axios, { type AxiosInstance } from "axios"
import config from "config"
import type { ICreateUser, IUpdateUser, IUser } from "types/userTypes"
import authService from "./authService"
import type { IUserReport } from "types/reportTypes"

class UserService {
  private http: AxiosInstance

  public constructor() {
    this.http = axios.create({
      baseURL: config.apiUrl,
      headers: {
        "Content-type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    })
  }

  public getOrganizationUsers = async (organizationId: string): Promise<Array<IUser>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get(`/organization/${organizationId}/user`, config)

    return data
  }

  public getUser = async (userId: string): Promise<IUser> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get(`/user/${userId}`, config)

    return data
  }

  public createUser = async (createUser: ICreateUser): Promise<IUser> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.post("/user", createUser, config)

    return data
  }

  public createUsersBatch = async (createUsersBatch: Array<ICreateUser>): Promise<Array<IUser>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.post("/user/batch", createUsersBatch, config)

    return data
  }

  public updateUser = async (userId: string, updateUser: IUpdateUser): Promise<IUser> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.put(`/user/${userId}`, updateUser, config)

    return data
  }

  public deleteUser = async (userId: string) => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.delete(`/user/${userId}`, config)

    return resp
  }

  public getBearerUser = async (): Promise<IUser> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<IUser>("/user/bearer", config)

    return data
  }

  public getUserReport = async (userId: string, period: number): Promise<IUserReport> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.get<IUserReport>(`/user/${userId}/report?period=${period}`, config)

    return resp.data
  }
}

const userService = new UserService()

export default userService
